import React, {useState, useEffect} from 'react';
import {Stack, Box} from '@mui/material';
import {t} from 'i18next';
import './QueryList.scss';
import {SessionSearch} from '../../types/SessionSearch';
import {Query} from '../../types/Query';
import ContentAndMetadata from './content-metadata/ContentAndMetadata';
import Conceptual from './conceptual/Conceptual';
import WorkProduct from './work-product/WorkProduct';
import Analytics from './analytics/Analytics';

import SortablePanel from './sortable-panel/SortablePanel';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from '@dnd-kit/modifiers';

export interface QueryListProps {
  userRoleId: number;
  item: SessionSearch;
  onQueryReordered?: (item: SessionSearch) => void;
  onQueryDeleted?: (item: SessionSearch) => void;
  onQueryModified?: (item: SessionSearch) => void;
}

interface ISortableItem {
  id: string;
  content: React.ReactNode;
}

const QueryList: React.FC<QueryListProps> = ({
  item,
  onQueryReordered,
  onQueryDeleted,
  onQueryModified,
  userRoleId,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [items, setItems] = useState<ISortableItem[]>([]);

  useEffect(() => {
    const sortableItems: ISortableItem[] = [];
    const queries = item.query ?? [];
    for (const query of queries) {
      let queryContent = <></>;
      let title = '';
      let tooltipKey = '';

      switch (query.type) {
        case 'ContentAndMetadata':
          queryContent = (
            <ContentAndMetadata
              query={query}
              indexID={item.indexId}
              onCriteriaChanged={handleCriteriaChanged}
            />
          );
          title = t('sessionSearch.queryList.contentAndMetadata.title');
          tooltipKey =
            'sessionSearch.queryList.contentAndMetadata.titleTooltip';
          break;
        case 'Conceptual':
          queryContent = (
            <Conceptual
              query={query}
              onTextChanged={(query) => handleCriteriaChanged(query)}
              userRoleId={userRoleId}
            />
          );
          title = t('sessionSearch.queryList.conceptual.title');
          tooltipKey = 'sessionSearch.queryList.conceptual.titleTooltip';
          break;
        case 'WorkProduct':
          queryContent = (
            <WorkProduct
              query={query}
              onCriteriaChanged={handleCriteriaChanged}
              userRoleId={userRoleId}
            />
          );
          title = t('sessionSearch.queryList.workProduct.title');
          tooltipKey = 'sessionSearch.queryList.workProduct.titleTooltip';
          break;
        case 'Analytics':
          queryContent = (
            <Analytics
              query={query}
              onCriteriaChanged={handleCriteriaChanged}
            />
          );
          title = t('sessionSearch.queryList.analytics.title');
          tooltipKey = 'sessionSearch.queryList.analytics.titleTooltip';
          break;
      }

      sortableItems.push({
        id: query.type,
        content: (
          <SortablePanel
            key={query.type}
            query={query}
            title={title}
            tooltip={tooltipKey}
            onDelete={handleQueryTypeDelete}
            onOperatorChanged={handleOperatorChanged}
            dataTestId={query.type}
          >
            {queryContent}
          </SortablePanel>
        ),
      });
    }

    setItems(sortableItems);
  }, [item]);

  const sensors = useSensors(
    // @dnd-kit
    // code for listening to mouse and keyboard events
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    // @dnd-kit
    // code for identifying which item is being reordered when the user starts dragging an item
    const {active} = event;
    const activeId = active?.id;
    if (activeId) {
      setActiveId(activeId.toString());
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    // @dnd-kit
    // code for reordering items when the user stops dragging an item
    const {active, over} = event;

    const activeId = active?.id;
    const overId = over?.id;
    if (activeId && overId && activeId !== overId) {
      const reorderItems = (items: ISortableItem[]) => {
        const oldIndex = items.findIndex((value) => value.id === activeId);
        const newIndex = items.findIndex((value) => value.id === overId);
        return arrayMove(items, oldIndex, newIndex);
      };

      const reorderedItems = reorderItems(items);
      setItems(reorderedItems);

      // Reorder the query items when the user updates the order in the UI
      const reorderedList = [];
      for (const reorderedItem of reorderedItems) {
        const queryItem = item.query?.find(
          (value) => value.type === reorderedItem.id
        );
        if (queryItem) {
          reorderedList.push(queryItem);
        }
      }

      const updatedItem = {
        ...item,
        query: reorderedList,
      };

      onQueryReordered && onQueryReordered(updatedItem);
    }

    setActiveId(null);
  };

  const handleQueryTypeDelete = (query: Query) => {
    const updatedItem = {
      ...item,
      query: item.query?.filter((value) => value.type !== query.type) ?? [],
    };

    onQueryDeleted && onQueryDeleted(updatedItem);
  };

  const handleOperatorChanged = (query: Query) => {
    const itemCopy = JSON.parse(JSON.stringify(item));

    const updatedQueries = (itemCopy.query ?? []).map((q: Query) => {
      if (q.type === query.type) {
        return {
          ...q,
          operator: query.operator,
        };
      }
      return q;
    });

    const updatedItem: SessionSearch = {
      ...itemCopy,
      query: updatedQueries,
    };

    onQueryModified && onQueryModified(updatedItem);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCriteriaChanged = (query: Query) => {
    const updatedItem = {
      ...item,
      query: item.query?.map((q) => (q.type === query.type ? query : q)) ?? [],
    };

    onQueryModified && onQueryModified(updatedItem);
  };

  const renderOverlay = (id: UniqueIdentifier) => {
    // Renders the UI for the item currently being dragged
    const item = items.find((value) => value.id === id);
    if (item) {
      return <Box className="sortable-panel_dragging">{item.content}</Box>;
    }
    return <></>;
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <Stack spacing={1} className="session-search-query-list">
          {items.map((item) => item.content)}
        </Stack>
      </SortableContext>
      <DragOverlay modifiers={[restrictToWindowEdges]}>
        {activeId ? renderOverlay(activeId) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default QueryList;
